import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {AssetListActions, AssetOverviewActions} from "../store/asset.actions";
import {selectAssetOverviewContent, selectAssetOverviewLoading, selectAssetOverviewParams, selectAssetOverviewTotalElements} from "../store/asset/asset.selectors";
import {AssetNewDialogComponent} from "../asset-new-dialog/asset-new-dialog.component";
import {CodeTableEnum} from "../../shared/model/code";
import {combineLatest, filter, skip, Subject, Subscription, take} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {Sort} from "@angular/material/sort";
import {PageEvent} from "@angular/material/paginator";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../shared/user-setting/store/user-setting.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {SelectionModel} from "@angular/cdk/collections";
import {Asset} from "../models/asset/asset";
import {ContextMenuComponent, ContextMenuItem} from "../../shared/components/context-menu/context-menu.component";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {CommentCountsActions} from "../../comment/store/comment.actions";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";

const TABLE_KEY = "asset-overview";
const COMMENT_CONTEXT = "/assets/overview/";

const ALL_COLUMN_GROUPS = [
    {label: "Main", cols: ["id", "name", "stage", "country", "currencyIso", "activityDescription", "gic"]},
];

const COLUMN_LABELS: Record<string, string> = {
    id: "ID",
    name: "Company Name",
    stage: "Stage",
    country: "Country",
    currencyIso: "Currency",
    activityDescription: "Activity Description",
    gic: "GIC",
};

const SORTABLE_COLUMNS = [
    {value: "id", label: "ID"},
    {value: "name", label: "Company Name"},
    {value: "stage", label: "Stage"},
    {value: "country", label: "Country"},
    {value: "currencyIso", label: "Currency"},
    {value: "activityDescription", label: "Activity Description"},
    {value: "gic", label: "GIC"},
];

@Component({
    selector: "valumize-asset-overview-page",
    templateUrl: "./asset-overview-page.component.html",
    styleUrls: ["./asset-overview-page.component.scss"],
    standalone: false,
    host: {class: "overview-page"}
})
export class AssetOverviewPageComponent implements OnInit, OnDestroy {
    private static readonly ALL_VALID_COLUMNS = ALL_COLUMN_GROUPS.flatMap(g => g.cols);

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);

    private readonly searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;
    private readonly settingsSubscription?: Subscription;
    private columnsSubscription?: Subscription;
    private dialogSubscription?: Subscription;
    private commentCountsSubscription?: Subscription;

    @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild("ctxMenu") contextMenu?: ContextMenuComponent;

    readonly selection = new SelectionModel<Asset>(true, [], true, (a, b) => a.id === b.id);
    selectMode = false;

    columns = ["id", "name", "stage", "country", "currencyIso", "activityDescription", "gic"];
    searchVisible = false;
    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    codeTableStage = CodeTableEnum.SHARED_STAGES;

    assets$ = this.store.select(selectAssetOverviewContent);
    totalElements$ = this.store.select(selectAssetOverviewTotalElements);
    params$ = this.store.select(selectAssetOverviewParams);
    isLoading$ = this.store.select(selectAssetOverviewLoading);

    sortActive = "name";
    sortDirection: "asc" | "desc" | "" = "asc";

    constructor() {
        this.settingsSubscription = this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1)
        ).subscribe(() => {
            this.store.dispatch(AssetListActions.open());
            this.applySettingsFromStore();
        });
    }

    ngOnInit() {
        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(search => {
            // Selection is kept across pagination (WI 992), but a search change filters the row
            // set — clear the selection so batch actions can't target now-invisible rows.
            this.selection.clear();
            this.store.dispatch(AssetOverviewActions.changeSearch({search}));
        });

        this.columnsSubscription = this.store.select(selectTableColumns(TABLE_KEY)).pipe(
            skip(1),
            filter((cols): cols is string[] => !!cols)
        ).subscribe(cols => {
            const valid = this.filterValidColumns(cols);
            if (valid) this.columns = valid;
        });

        // Page-level comment indicator: load counts for this page's context, refreshed on dataset switch.
        this.commentCountsSubscription = this.store.select(selectSelectedSourceDataset).pipe(
            distinctUntilChanged()
        ).subscribe(() => this.store.dispatch(CommentCountsActions.load({inContext: COMMENT_CONTEXT})));
    }

    get displayedColumns(): string[] {
        return this.selectMode ? ["select", ...this.columns] : this.columns;
    }

    enableSelectMode(row: Asset) {
        this.selectMode = true;
        this.selection.select(row);
    }

    exitSelectMode() {
        this.selection.clear();
        this.selectMode = false;
    }

    onRowContextMenu(event: MouseEvent, row: Asset) {
        // While multi-selecting, the context menu manages the clicked row's membership in the batch
        // (the toolbar handles the batch-wide actions).
        if (this.selectMode) {
            const isSelected = this.selection.isSelected(row);
            const selectItems: ContextMenuItem[] = [
                {
                    label: isSelected ? "Deselect" : "Select",
                    icon: isSelected ? "check_box_outline_blank" : "check_box",
                    action: () => this.selection.toggle(row)
                },
                {
                    label: "Clear selection",
                    icon: "deselect",
                    action: () => this.exitSelectMode()
                }
            ];
            this.contextMenu?.open(event, selectItems);
            return;
        }
        // Right-clicking a row targets just that row.
        if (!this.selection.isSelected(row)) {
            this.selection.clear();
            this.selection.select(row);
        }
        const targets = this.selection.selected;
        const allArchived = targets.every(asset => asset.isArchived?.value);
        const items: ContextMenuItem[] = [
            {
                label: "Select",
                icon: "check_box",
                action: () => this.enableSelectMode(row)
            },
            {
                label: allArchived ? "Unarchive" : "Archive",
                icon: allArchived ? "unarchive" : "archive",
                action: () => this.archiveSelected(!allArchived)
            }
        ];
        this.contextMenu?.open(event, items);
    }

    archiveSelected(archived: boolean) {
        const assets = this.selection.selected;
        if (assets.length === 0) {
            return;
        }
        if (assets.length === 1) {
            this.dispatchArchive(assets, archived);
            return;
        }
        const verb = archived ? "Archive" : "Unarchive";
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `${verb} ${assets.length} companies?`;
        dialogRef.componentInstance.htmlContent = `<p>Are you sure you want to ${verb.toLowerCase()} these ${assets.length} companies?</p>`;
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.dispatchArchive(assets, archived);
            }
        });
    }

    private dispatchArchive(assets: Asset[], archived: boolean) {
        this.store.dispatch(AssetOverviewActions.archiveAssets({assets, archived}));
        this.selection.clear();
        this.selectMode = false;
    }

    openNewAssetDialog() {
        this.dialog.open(AssetNewDialogComponent);
    }

    openTableSettings() {
        this.store.select(selectAssetOverviewParams).pipe(take(1)).subscribe(params => {
            const sortParts = params.sort.split(",");
            const currentSort = {field: sortParts[0] || "name", direction: sortParts[1] === "desc" ? "desc" : "asc"} as const;

            const dialogData: TableSettingsDialogData = {
                tableKey: TABLE_KEY,
                columnGroups: ALL_COLUMN_GROUPS,
                columnLabels: COLUMN_LABELS,
                currentColumns: this.columns,
                currentPageSize: params.size,
                currentSort,
                pageSizeOptions: [10, 20, 30, 50, 100],
                sortableColumns: SORTABLE_COLUMNS,
                isAdmin: false,
                defaultColumns: AssetOverviewPageComponent.ALL_VALID_COLUMNS,
                defaultPageSize: 20,
                defaultSort: {field: "name", direction: "asc"},
            };

            this.dialogSubscription = this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
                .afterClosed()
                .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
        });
    }

    onArchivedChanged(checked: boolean) {
        // Toggling the archived filter changes which rows are visible — clear the selection so
        // batch actions can't target rows that left the view.
        this.selection.clear();
        this.store.dispatch(AssetOverviewActions.changeArchived({archived: checked}));
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        if (this.searchVisible) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
        } else {
            this.searchSubject.next("");
        }
    }

    onSearchInput(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.searchSubject.next(value);
    }

    onSortChange(sort: Sort) {
        if (sort.direction) {
            this.sortActive = sort.active;
            this.sortDirection = sort.direction;
        } else {
            this.sortActive = "name";
            this.sortDirection = "asc";
        }
        const sortString = `${this.sortActive},${this.sortDirection}`;
        this.store.dispatch(AssetOverviewActions.changeSort({sort: sortString}));
    }

    onPageChange(event: PageEvent) {
        this.store.dispatch(AssetOverviewActions.changePage({page: event.pageIndex, size: event.pageSize}));
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.settingsSubscription?.unsubscribe();
        this.columnsSubscription?.unsubscribe();
        this.dialogSubscription?.unsubscribe();
        this.commentCountsSubscription?.unsubscribe();
    }

    private applySettingsFromStore() {
        combineLatest([
            this.store.select(selectTableColumns(TABLE_KEY)),
            this.store.select(selectTablePageSize(TABLE_KEY)),
            this.store.select(selectTableSort(TABLE_KEY))
        ]).pipe(take(1)).subscribe(([columns, pageSize, sort]) => {
            if (columns) {
                const valid = this.filterValidColumns(columns);
                if (valid) this.columns = valid;
            }
            if (sort) {
                this.sortActive = sort.field;
                this.sortDirection = sort.direction as "asc" | "desc";
            }
            this.store.dispatch(AssetOverviewActions.applySettings({
                size: pageSize ?? undefined,
                sort: sort ? `${sort.field},${sort.direction}` : undefined
            }));
            this.store.dispatch(AssetOverviewActions.search());
        });
    }

    private filterValidColumns(columns: string[]): string[] | null {
        const valid = columns.filter(c => AssetOverviewPageComponent.ALL_VALID_COLUMNS.includes(c));
        return valid.length > 0 ? valid : null;
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;

        this.columns = result.columns;
        this.sortActive = result.sort.field;
        this.sortDirection = result.sort.direction as "asc" | "desc";
        this.store.dispatch(AssetOverviewActions.applySettings({
            size: result.pageSize,
            sort: `${result.sort.field},${result.sort.direction}`
        }));
        this.store.dispatch(AssetOverviewActions.search());

        const payload = {tableKey: TABLE_KEY, columns: result.columns, pageSize: result.pageSize, sort: result.sort};

        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }
}
